"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getPrecomputedInstance = exports.offlinePrecomputedInit = exports.precomputedInit = exports.EppoPrecomputedJSClient = exports.getConfigUrl = exports.getInstance = exports.init = exports.offlineInit = exports.buildStorageKeySuffix = exports.EppoJSClient = exports.NO_OP_EVENT_DISPATCHER = exports.ChromeStorageEngine = exports.EppoAssignmentLogger = void 0;
const js_client_sdk_common_1 = require("@eppo/js-client-sdk-common");
const assignment_cache_factory_1 = require("./cache/assignment-cache-factory");
const hybrid_assignment_cache_1 = require("./cache/hybrid-assignment-cache");
const config_loader_1 = require("./config-loader");
const configuration_factory_1 = require("./configuration-factory");
const browser_network_status_listener_1 = require("./events/browser-network-status-listener");
const local_storage_backed_named_event_queue_1 = require("./events/local-storage-backed-named-event-queue");
const sdk_data_1 = require("./sdk-data");
// Export the common types and classes from the SDK.
var js_client_sdk_common_2 = require("@eppo/js-client-sdk-common");
Object.defineProperty(exports, "EppoAssignmentLogger", { enumerable: true, get: function () { return js_client_sdk_common_2.EppoAssignmentLogger; } });
var chrome_storage_engine_1 = require("./chrome-storage-engine");
Object.defineProperty(exports, "ChromeStorageEngine", { enumerable: true, get: function () { return chrome_storage_engine_1.ChromeStorageEngine; } });
// Instantiate the configuration store with memory-only implementation.
const flagConfigurationStore = (0, configuration_factory_1.configurationStorageFactory)({
    forceMemoryOnly: true,
});
// Instantiate the precomputed flags and bandits stores with memory-only implementation.
const memoryOnlyPrecomputedFlagsStore = (0, configuration_factory_1.precomputedFlagsStorageFactory)();
const memoryOnlyPrecomputedBanditsStore = (0, configuration_factory_1.precomputedBanditStoreFactory)();
exports.NO_OP_EVENT_DISPATCHER = {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    attachContext: () => { },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    dispatch: () => { },
};
/**
 * Client for assigning experiment variations.
 * @public
 */
class EppoJSClient extends js_client_sdk_common_1.EppoClient {
    constructor() {
        super(...arguments);
        this.initialized = false;
    }
    getStringAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        this.ensureInitialized();
        return super.getStringAssignment(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getStringAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue) {
        this.ensureInitialized();
        return super.getStringAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    /**
     * @deprecated Use getBooleanAssignment instead
     */
    getBoolAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        return this.getBooleanAssignment(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getBooleanAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        this.ensureInitialized();
        return super.getBooleanAssignment(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getBooleanAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue) {
        this.ensureInitialized();
        return super.getBooleanAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getIntegerAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        this.ensureInitialized();
        return super.getIntegerAssignment(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getIntegerAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue) {
        this.ensureInitialized();
        return super.getIntegerAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getNumericAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        this.ensureInitialized();
        return super.getNumericAssignment(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getNumericAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue) {
        this.ensureInitialized();
        return super.getNumericAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getJSONAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        this.ensureInitialized();
        return super.getJSONAssignment(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getJSONAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue) {
        this.ensureInitialized();
        return super.getJSONAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getBanditAction(flagKey, subjectKey, subjectAttributes, actions, defaultValue) {
        this.ensureInitialized();
        return super.getBanditAction(flagKey, subjectKey, subjectAttributes, actions, defaultValue);
    }
    getBanditActionDetails(flagKey, subjectKey, subjectAttributes, actions, defaultValue) {
        this.ensureInitialized();
        return super.getBanditActionDetails(flagKey, subjectKey, subjectAttributes, actions, defaultValue);
    }
    getExperimentContainerEntry(flagExperiment, subjectKey, subjectAttributes) {
        this.ensureInitialized();
        return super.getExperimentContainerEntry(flagExperiment, subjectKey, subjectAttributes);
    }
    ensureInitialized() {
        if (!this.initialized) {
            js_client_sdk_common_1.applicationLogger.warn('Eppo SDK assignment requested before init() completed');
        }
    }
    /**
     * @internal
     */
    async init(config) {
        var _a;
        js_client_sdk_common_1.validation.validateNotBlank(config.apiKey, 'API key required');
        let initializationError;
        const { apiKey, persistentStore, baseUrl, maxCacheAgeSeconds, updateOnFetch, requestTimeoutMs, numInitialRequestRetries, numPollRequestRetries, pollingIntervalMs, pollAfterSuccessfulInitialization = false, pollAfterFailedInitialization = false, skipInitialRequest = false, eventIngestionConfig, enableOverrides = false, } = config;
        try {
            // If any existing instances; ensure they are not polling
            this.stopPolling();
            // Set up assignment logger and cache
            this.setAssignmentLogger(config.assignmentLogger);
            if (config.banditLogger) {
                this.setBanditLogger(config.banditLogger);
            }
            // Default to obfuscated mode when requesting configuration from the server.
            this.setIsObfuscated(true);
            const storageKeySuffix = buildStorageKeySuffix(apiKey);
            // Set the configuration store to the desired persistent store, if provided.
            // Otherwise, the factory method will detect the current environment and instantiate the correct store.
            const configurationStore = (0, configuration_factory_1.configurationStorageFactory)({
                maxAgeSeconds: maxCacheAgeSeconds,
                servingStoreUpdateStrategy: updateOnFetch,
                persistentStore,
                hasChromeStorage: (0, configuration_factory_1.hasChromeStorage)(),
                hasWindowLocalStorage: (0, configuration_factory_1.hasWindowLocalStorage)(),
            }, {
                chromeStorage: (0, configuration_factory_1.chromeStorageIfAvailable)(),
                windowLocalStorage: (0, configuration_factory_1.localStorageIfAvailable)(),
                storageKeySuffix,
            });
            // Client-side config should be obfuscated by default.
            // This has the effect of casting any entries stored in the persistent store as obfuscated.
            // We do this because we don't store any metadata in the persistent store and can make the assumption that entries
            // in the persistent store are obfuscated since the reason behind obfuscation is to obfuscate any data stored on a
            // user device.
            this.setIsObfuscated(true); // Use deprecated method to silence warning logs.
            configurationStore.setFormat(js_client_sdk_common_1.FormatEnum.CLIENT);
            this.setFlagConfigurationStore(configurationStore);
            if (enableOverrides) {
                const overrideStore = (0, configuration_factory_1.overrideStorageFactory)({
                    hasWindowLocalStorage: (0, configuration_factory_1.hasWindowLocalStorage)(),
                }, {
                    windowLocalStorage: (0, configuration_factory_1.localStorageIfAvailable)(),
                    storageKey: config.overridesStorageKey,
                });
                this.setOverrideStore(overrideStore);
            }
            else {
                this.unsetOverrideStore();
            }
            // instantiate and init assignment cache
            const assignmentCache = (0, assignment_cache_factory_1.assignmentCacheFactory)({
                chromeStorage: (0, configuration_factory_1.chromeStorageIfAvailable)(),
                storageKeySuffix,
            });
            if (assignmentCache instanceof hybrid_assignment_cache_1.default) {
                await assignmentCache.init();
            }
            this.useCustomAssignmentCache(assignmentCache);
            // Set up parameters for requesting updated configurations
            const requestConfiguration = {
                apiKey,
                sdkName: sdk_data_1.sdkName,
                sdkVersion: sdk_data_1.sdkVersion,
                baseUrl,
                requestTimeoutMs,
                numInitialRequestRetries,
                numPollRequestRetries,
                pollAfterSuccessfulInitialization,
                pollAfterFailedInitialization,
                pollingIntervalMs,
                throwOnFailedInitialization: true,
                skipInitialPoll: skipInitialRequest,
            };
            this.setConfigurationRequestParameters(requestConfiguration);
            this.setEventDispatcher(newEventDispatcher(apiKey, eventIngestionConfig));
            // We have two at-bats for initialization: from the configuration store and from fetching
            // We can resolve the initialization promise as soon as either one succeeds
            // If there is no persistent store underlying the ConfigurationStore, then there is no race to begin with, as
            // there is not an async local load to wait for.
            // The definition of a successful configuration load differs for a cached config vs a fetched config.
            // Specifically, a cached config with no entries is not considered a completed load and will result in
            // `ConfigLoaderStatus.DID_NOT_PRODUCE` while and empty config from the fetch is considered a valid, `COMPLETED`
            //  configuration load.
            let initFromConfigStoreError = undefined;
            let initFromFetchError = undefined;
            const attemptInitFromConfigStore = configurationStore
                .init()
                .then(async () => {
                if (!configurationStore.getKeys().length) {
                    // Consider empty configuration stores invalid
                    js_client_sdk_common_1.applicationLogger.warn('Eppo SDK cached configuration is empty');
                    initFromConfigStoreError = new Error('Configuration store was empty');
                    return config_loader_1.ConfigLoaderStatus.DID_NOT_PRODUCE;
                }
                const cacheIsExpired = await configurationStore.isExpired();
                if (cacheIsExpired && !config.useExpiredCache) {
                    js_client_sdk_common_1.applicationLogger.warn('Eppo SDK set not to use expired cached configuration');
                    initFromConfigStoreError = new Error('Configuration store was expired');
                    return config_loader_1.ConfigLoaderStatus.DID_NOT_PRODUCE;
                }
                else if (cacheIsExpired && config.useExpiredCache) {
                    js_client_sdk_common_1.applicationLogger.warn('Eppo SDK config.useExpiredCache is true; using expired cache');
                }
                return config_loader_1.ConfigLoaderStatus.COMPLETED;
            })
                .catch((e) => {
                js_client_sdk_common_1.applicationLogger.warn('Eppo SDK encountered an error initializing from the configuration store', e);
                initFromConfigStoreError = e;
                return config_loader_1.ConfigLoaderStatus.FAILED;
            })
                .then((status) => {
                return { source: config_loader_1.ConfigSource.CONFIG_STORE, result: status };
            });
            // Kick off a remote fetch (and start the poller, if configured to do so).
            // Before a network fetch is attempted, the `ConfiguratonRequestor` instance will check to see whether the
            // locally-stored config data is expired. If it is not expired, the network fetch is skipped, and the promise chain
            // will quickly return. **When data not successfully fetched due to an error, invalid data, or giving up the fetch
            // because the cache is not expired, the configuration store will not be set as initialized.**
            const attemptInitFromFetch = this.fetchFlagConfigurations()
                .then(() => {
                if (configurationStore.isInitialized()) {
                    // If fetch has won the race and the configStore is initialized, that means we have a successful load and the
                    // fetched data was used to init.
                    return config_loader_1.ConfigLoaderStatus.COMPLETED;
                }
                else {
                    // If the config store is not initialized and the fetch says it is done, that means the fetch did not set any
                    // values into the config store (ex:the cache is not expired or invalid config data was received).
                    // It's important not to set an error here as this state does not mean that an error was encountered.
                    // If the initial cache had not expired, the fetch would be skipped and the config store would report as
                    // uninitialized because only a successful fetch setting the data entries will set the persistent store as
                    // initialized.
                    return config_loader_1.ConfigLoaderStatus.DID_NOT_PRODUCE;
                }
            })
                .catch((e) => {
                js_client_sdk_common_1.applicationLogger.warn('Eppo SDK encountered an error initializing from fetching', e);
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                initFromFetchError = e;
                return config_loader_1.ConfigLoaderStatus.FAILED;
            })
                .then((status) => {
                return { source: config_loader_1.ConfigSource.FETCH, result: status };
            });
            const racers = [attemptInitFromConfigStore];
            // attemptInitFromFetch will return early and not attempt to fetch if `skipInitialRequest` is `true`.
            // We could still race it against a load from configuration as the handling logic will see that no config was loaded
            // into the config store from the remote fetch and return `DID_NOT_PRODUCE`.
            if (!config.skipInitialRequest) {
                racers.push(attemptInitFromFetch);
            }
            let initializationSource;
            const { source: firstSource, result: firstConfigResult } = await Promise.race(racers);
            if (firstConfigResult === config_loader_1.ConfigLoaderStatus.COMPLETED) {
                // First config load successfully produced a configuration
                initializationSource = firstSource;
            }
            else {
                // First attempt failed or did not produce configuration, but we have a second at bat that will be executed in the scope of the top-level try-catch
                const secondAttempt = firstSource === config_loader_1.ConfigSource.FETCH ? attemptInitFromConfigStore : attemptInitFromFetch;
                initializationSource = await secondAttempt.then((resultPair) => {
                    const { source, result } = resultPair;
                    return result === config_loader_1.ConfigLoaderStatus.COMPLETED ? source : config_loader_1.ConfigSource.NONE;
                });
            }
            if (initializationSource === config_loader_1.ConfigSource.NONE) {
                // Neither config loader produced useful config. Return error, if exists, in order from first to last: fetch, config store, generic.
                initializationError = initFromFetchError
                    ? initFromFetchError
                    : initFromConfigStoreError
                        ? initFromConfigStoreError
                        : new Error('Eppo SDK: No configuration source produced a valid configuration');
            }
            js_client_sdk_common_1.applicationLogger.debug('Initialization source', initializationSource);
        }
        catch (error) {
            initializationError = error instanceof Error ? error : new Error(String(error));
        }
        if (initializationError) {
            js_client_sdk_common_1.applicationLogger.warn('Eppo SDK was unable to initialize with a configuration, assignment calls will return the default value and not be logged' +
                (config.pollAfterFailedInitialization
                    ? ' until an experiment configuration is successfully retrieved'
                    : ''));
            if ((_a = config.throwOnFailedInitialization) !== null && _a !== void 0 ? _a : true) {
                throw initializationError;
            }
        }
        this.initialized = true;
        return this;
    }
    /**
     * @internal
     */
    offlineInit(config) {
        var _a, _b, _c;
        const isObfuscated = (_a = config.isObfuscated) !== null && _a !== void 0 ? _a : false;
        const throwOnFailedInitialization = (_b = config.throwOnFailedInitialization) !== null && _b !== void 0 ? _b : true;
        const enableOverrides = (_c = config.enableOverrides) !== null && _c !== void 0 ? _c : false;
        try {
            const memoryOnlyConfigurationStore = (0, configuration_factory_1.configurationStorageFactory)({
                forceMemoryOnly: true,
            });
            memoryOnlyConfigurationStore.setFormat(isObfuscated ? js_client_sdk_common_1.FormatEnum.CLIENT : js_client_sdk_common_1.FormatEnum.SERVER);
            memoryOnlyConfigurationStore
                .setEntries(config.flagsConfiguration)
                .catch((err) => js_client_sdk_common_1.applicationLogger.warn('Error setting flags for memory-only configuration store', err));
            this.setFlagConfigurationStore(memoryOnlyConfigurationStore);
            if (enableOverrides) {
                const overrideStore = (0, configuration_factory_1.overrideStorageFactory)({
                    hasWindowLocalStorage: (0, configuration_factory_1.hasWindowLocalStorage)(),
                }, {
                    windowLocalStorage: (0, configuration_factory_1.localStorageIfAvailable)(),
                    storageKey: config.overridesStorageKey,
                });
                this.setOverrideStore(overrideStore);
            }
            else {
                this.unsetOverrideStore();
            }
            // Allow the caller to override the default obfuscated mode, which is false
            // since the purpose of this method is to bootstrap the SDK from an external source,
            // which is likely a server that has not-obfuscated flag values.
            this.setIsObfuscated(isObfuscated);
            if (config.assignmentLogger) {
                this.setAssignmentLogger(config.assignmentLogger);
            }
            if (config.banditLogger) {
                this.setBanditLogger(config.banditLogger);
            }
            // There is no SDK key in the offline context.
            const storageKeySuffix = 'offline';
            // As this is a synchronous initialization,
            // we are unable to call the async `init` method on the assignment cache
            // which loads the assignment cache from the browser's storage.
            // Therefore, there is no purpose trying to use a persistent assignment cache.
            const assignmentCache = (0, assignment_cache_factory_1.assignmentCacheFactory)({
                storageKeySuffix,
                forceMemoryOnly: true,
            });
            this.useCustomAssignmentCache(assignmentCache);
        }
        catch (error) {
            js_client_sdk_common_1.applicationLogger.warn('Eppo SDK encountered an error initializing, assignment calls will return the default value and not be logged');
            if (throwOnFailedInitialization) {
                throw error;
            }
        }
        this.initialized = true;
    }
}
exports.EppoJSClient = EppoJSClient;
// Ensure that the client is instantiated during class loading.
// Use an empty memory-only configuration store until the `init` method is called,
// to avoid serving stale data to the user.
/**
 * @deprecated Use `getInstance()` instead.
 */
EppoJSClient.instance = new EppoJSClient({
    flagConfigurationStore,
    isObfuscated: true,
});
EppoJSClient.initialized = false;
/**
 * Builds a storage key suffix from an API key.
 * @param apiKey - The API key to build the suffix from
 * @returns A string suffix for storage keys
 * @public
 */
function buildStorageKeySuffix(apiKey) {
    // Note that we use the first 8 characters of the API key to create per-API key persistent storages and caches
    return apiKey.replace(/\W/g, '').substring(0, 8);
}
exports.buildStorageKeySuffix = buildStorageKeySuffix;
/**
 * Initializes the Eppo client with configuration parameters.
 *
 * The purpose is for use-cases where the configuration is available from an external process
 * that can bootstrap the SDK.
 *
 * This method should be called once on application startup.
 *
 * @param config - client configuration
 * @returns a singleton client instance
 * @public
 */
function offlineInit(config) {
    const instance = getInstance();
    instance.offlineInit(config);
    // For backwards compatibility.
    EppoJSClient.initialized = true;
    return instance;
}
exports.offlineInit = offlineInit;
/**
 * Tracks pending initialization. After an initialization completes, this value is set to null
 */
let initializationPromise = null;
/**
 * Initializes the Eppo client with configuration parameters.
 * This method should be called once on application startup.
 * @param config - client configuration
 * @public
 */
async function init(config) {
    js_client_sdk_common_1.validation.validateNotBlank(config.apiKey, 'API key required');
    const instance = getInstance();
    if (EppoJSClient.initialized) {
        if (config.forceReinitialize) {
            js_client_sdk_common_1.applicationLogger.info('Eppo SDK is already initialized, reinitializing since forceReinitialize is true.');
        }
        else {
            js_client_sdk_common_1.applicationLogger.warn('Eppo SDK is already initialized, skipping reinitialization since forceReinitialize is false.');
            return instance;
        }
    }
    if (initializationPromise === null) {
        initializationPromise = instance.init(config);
    }
    else {
        js_client_sdk_common_1.applicationLogger.warn('Initialization is already in progress. init should be called only once at application startup.');
    }
    const client = await initializationPromise;
    initializationPromise = null;
    // For backwards compatibility.
    EppoJSClient.initialized = true;
    return client;
}
exports.init = init;
/**
 * Used to access a singleton SDK client instance.
 * Use the method after calling init() to initialize the client.
 * @returns a singleton client instance
 * @public
 */
function getInstance() {
    return EppoJSClient.instance;
}
exports.getInstance = getInstance;
/**
 * Used to build the URL for fetching the flag configuration.
 * @returns a URL string
 * @public
 */
function getConfigUrl(apiKey, baseUrl) {
    const queryParams = { sdkName: sdk_data_1.sdkName, sdkVersion: sdk_data_1.sdkVersion, apiKey };
    return new js_client_sdk_common_1.ApiEndpoints({ baseUrl, queryParams }).ufcEndpoint();
}
exports.getConfigUrl = getConfigUrl;
/**
 * Client for assigning precomputed experiment variations.
 * @public
 */
class EppoPrecomputedJSClient extends js_client_sdk_common_1.EppoPrecomputedClient {
    getStringAssignment(flagKey, defaultValue) {
        EppoPrecomputedJSClient.getAssignmentInitializationCheck();
        return super.getStringAssignment(flagKey, defaultValue);
    }
    getBooleanAssignment(flagKey, defaultValue) {
        EppoPrecomputedJSClient.getAssignmentInitializationCheck();
        return super.getBooleanAssignment(flagKey, defaultValue);
    }
    getIntegerAssignment(flagKey, defaultValue) {
        EppoPrecomputedJSClient.getAssignmentInitializationCheck();
        return super.getIntegerAssignment(flagKey, defaultValue);
    }
    getNumericAssignment(flagKey, defaultValue) {
        EppoPrecomputedJSClient.getAssignmentInitializationCheck();
        return super.getNumericAssignment(flagKey, defaultValue);
    }
    getJSONAssignment(flagKey, defaultValue) {
        EppoPrecomputedJSClient.getAssignmentInitializationCheck();
        return super.getJSONAssignment(flagKey, defaultValue);
    }
    getBanditAction(flagKey, defaultValue) {
        EppoPrecomputedJSClient.getAssignmentInitializationCheck();
        return super.getBanditAction(flagKey, defaultValue);
    }
    static getAssignmentInitializationCheck() {
        if (!EppoJSClient.initialized) {
            js_client_sdk_common_1.applicationLogger.warn('Eppo SDK assignment requested before init() completed');
        }
    }
}
exports.EppoPrecomputedJSClient = EppoPrecomputedJSClient;
EppoPrecomputedJSClient.instance = new EppoPrecomputedJSClient({
    precomputedFlagStore: memoryOnlyPrecomputedFlagsStore,
    subject: {
        subjectKey: '',
        subjectAttributes: {},
    },
});
EppoPrecomputedJSClient.initialized = false;
/**
 * Initializes the Eppo precomputed client with configuration parameters.
 * This method should be called once on application startup.
 * @param config - client configuration
 * @public
 */
async function precomputedInit(config) {
    if (EppoPrecomputedJSClient.initialized) {
        return EppoPrecomputedJSClient.instance;
    }
    js_client_sdk_common_1.validation.validateNotBlank(config.apiKey, 'API key required');
    js_client_sdk_common_1.validation.validateNotBlank(config.precompute.subjectKey, 'Subject key required');
    const { apiKey, precompute: { subjectKey, subjectAttributes = {}, banditActions }, baseUrl, requestTimeoutMs, numInitialRequestRetries, numPollRequestRetries, pollingIntervalMs, pollAfterSuccessfulInitialization = false, pollAfterFailedInitialization = false, skipInitialRequest = false, enableOverrides = false, overridesStorageKey, } = config;
    // Add assignment cache initialization
    const storageKeySuffix = buildStorageKeySuffix(apiKey);
    const assignmentCache = (0, assignment_cache_factory_1.assignmentCacheFactory)({
        chromeStorage: (0, configuration_factory_1.chromeStorageIfAvailable)(),
        storageKeySuffix,
    });
    if (assignmentCache instanceof hybrid_assignment_cache_1.default) {
        await assignmentCache.init();
    }
    // Set up parameters for requesting updated configurations
    const requestParameters = {
        apiKey,
        sdkName: sdk_data_1.sdkName,
        sdkVersion: sdk_data_1.sdkVersion,
        baseUrl,
        requestTimeoutMs,
        numInitialRequestRetries,
        numPollRequestRetries,
        pollAfterSuccessfulInitialization,
        pollAfterFailedInitialization,
        pollingIntervalMs,
        throwOnFailedInitialization: true,
        skipInitialPoll: skipInitialRequest,
    };
    const subject = { subjectKey, subjectAttributes };
    EppoPrecomputedJSClient.instance = new EppoPrecomputedJSClient({
        precomputedFlagStore: memoryOnlyPrecomputedFlagsStore,
        requestParameters,
        subject,
        precomputedBanditStore: memoryOnlyPrecomputedBanditsStore,
        banditActions,
    });
    // Set up overrides if enabled
    if (enableOverrides) {
        const overrideStore = (0, configuration_factory_1.overrideStorageFactory)({
            hasWindowLocalStorage: (0, configuration_factory_1.hasWindowLocalStorage)(),
        }, {
            windowLocalStorage: (0, configuration_factory_1.localStorageIfAvailable)(),
            storageKey: overridesStorageKey,
        });
        EppoPrecomputedJSClient.instance.setOverrideStore(overrideStore);
    }
    else {
        EppoPrecomputedJSClient.instance.unsetOverrideStore();
    }
    EppoPrecomputedJSClient.instance.setAssignmentLogger(config.assignmentLogger);
    if (config.banditLogger) {
        EppoPrecomputedJSClient.instance.setBanditLogger(config.banditLogger);
    }
    EppoPrecomputedJSClient.instance.useCustomAssignmentCache(assignmentCache);
    await EppoPrecomputedJSClient.instance.fetchPrecomputedFlags();
    EppoPrecomputedJSClient.initialized = true;
    return EppoPrecomputedJSClient.instance;
}
exports.precomputedInit = precomputedInit;
/**
 * Initializes the Eppo precomputed client with configuration parameters.
 *
 * The purpose is for use-cases where the precomputed assignments are available from an external process
 * that can bootstrap the SDK.
 *
 * This method should be called once on application startup.
 *
 * @param config - precomputed client configuration
 * @returns a singleton precomputed client instance
 * @public
 */
function offlinePrecomputedInit(config) {
    var _a, _b;
    const throwOnFailedInitialization = (_a = config.throwOnFailedInitialization) !== null && _a !== void 0 ? _a : true;
    const enableOverrides = (_b = config.enableOverrides) !== null && _b !== void 0 ? _b : false;
    let configurationWire;
    try {
        configurationWire = JSON.parse(config.precomputedConfiguration);
        if (!configurationWire.precomputed)
            throw new Error();
    }
    catch (error) {
        const errorMessage = 'Invalid precomputed configuration wire';
        if (throwOnFailedInitialization) {
            throw new Error(errorMessage);
        }
        js_client_sdk_common_1.applicationLogger.error(`[Eppo SDK] ${errorMessage}`);
        return EppoPrecomputedJSClient.instance;
    }
    const { subjectKey, subjectAttributes, response } = configurationWire.precomputed;
    const parsedResponse = JSON.parse(response);
    try {
        const memoryOnlyPrecomputedStore = (0, configuration_factory_1.precomputedFlagsStorageFactory)();
        memoryOnlyPrecomputedStore
            .setEntries(parsedResponse.flags)
            .catch((err) => js_client_sdk_common_1.applicationLogger.warn('Error setting precomputed assignments for memory-only store', err));
        memoryOnlyPrecomputedStore.salt = parsedResponse.salt;
        const memoryOnlyPrecomputedBanditStore = (0, configuration_factory_1.precomputedBanditStoreFactory)();
        memoryOnlyPrecomputedBanditStore
            .setEntries(parsedResponse.bandits)
            .catch((err) => js_client_sdk_common_1.applicationLogger.warn('Error setting precomputed bandits for memory-only store', err));
        memoryOnlyPrecomputedBanditStore.salt = parsedResponse.salt;
        const subject = {
            subjectKey,
            subjectAttributes: subjectAttributes !== null && subjectAttributes !== void 0 ? subjectAttributes : {},
        };
        shutdownEppoPrecomputedClient();
        // Add memory-only assignment cache for offline mode
        const assignmentCache = (0, assignment_cache_factory_1.assignmentCacheFactory)({
            storageKeySuffix: 'offline',
            forceMemoryOnly: true,
        });
        EppoPrecomputedJSClient.instance = new EppoPrecomputedJSClient({
            precomputedFlagStore: memoryOnlyPrecomputedStore,
            precomputedBanditStore: memoryOnlyPrecomputedBanditStore,
            subject,
        });
        // Set up overrides if enabled
        if (enableOverrides) {
            const overrideStore = (0, configuration_factory_1.overrideStorageFactory)({
                hasWindowLocalStorage: (0, configuration_factory_1.hasWindowLocalStorage)(),
            }, {
                windowLocalStorage: (0, configuration_factory_1.localStorageIfAvailable)(),
                storageKey: config.overridesStorageKey,
            });
            EppoPrecomputedJSClient.instance.setOverrideStore(overrideStore);
        }
        else {
            EppoPrecomputedJSClient.instance.unsetOverrideStore();
        }
        if (config.assignmentLogger) {
            EppoPrecomputedJSClient.instance.setAssignmentLogger(config.assignmentLogger);
        }
        if (config.banditLogger) {
            EppoPrecomputedJSClient.instance.setBanditLogger(config.banditLogger);
        }
        EppoPrecomputedJSClient.instance.useCustomAssignmentCache(assignmentCache);
    }
    catch (error) {
        js_client_sdk_common_1.applicationLogger.warn('[Eppo SDK] Encountered an error initializing precomputed client, assignment calls will return the default value and not be logged');
        if (throwOnFailedInitialization) {
            throw error;
        }
    }
    EppoPrecomputedJSClient.initialized = true;
    return EppoPrecomputedJSClient.instance;
}
exports.offlinePrecomputedInit = offlinePrecomputedInit;
function shutdownEppoPrecomputedClient() {
    if (EppoPrecomputedJSClient.initialized) {
        EppoPrecomputedJSClient.instance.stopPolling();
        EppoPrecomputedJSClient.initialized = false;
        js_client_sdk_common_1.applicationLogger.warn('[Eppo SDK] Precomputed client is being re-initialized.');
    }
}
/**
 * Used to access a singleton SDK precomputed client instance.
 * Use the method after calling precomputedInit() to initialize the client.
 * @returns a singleton precomputed client instance
 * @public
 */
function getPrecomputedInstance() {
    return EppoPrecomputedJSClient.instance;
}
exports.getPrecomputedInstance = getPrecomputedInstance;
function newEventDispatcher(sdkKey, config = {}) {
    // initialize config with default values
    const { batchSize = 1000, deliveryIntervalMs = 10000, disabled = false, maxQueueSize = 10000, maxRetries = 3, maxRetryDelayMs = 30000, retryIntervalMs = 5000, } = config;
    if (disabled) {
        return exports.NO_OP_EVENT_DISPATCHER;
    }
    const eventQueue = (0, configuration_factory_1.hasWindowLocalStorage)()
        ? new local_storage_backed_named_event_queue_1.default('events')
        : new js_client_sdk_common_1.BoundedEventQueue('events', [], maxQueueSize);
    const emptyNetworkStatusListener = 
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    { isOffline: () => false, onNetworkStatusChange: () => { } };
    const networkStatusListener = typeof window !== 'undefined' ? new browser_network_status_listener_1.default() : emptyNetworkStatusListener;
    return (0, js_client_sdk_common_1.newDefaultEventDispatcher)(eventQueue, networkStatusListener, sdkKey, batchSize, {
        deliveryIntervalMs,
        retryIntervalMs,
        maxRetryDelayMs,
        maxRetries,
    });
}
//# sourceMappingURL=index.js.map